function normalizeHtml(html) {
  return String(html || "")
    .replace(/<\s*\/\s*br\s*>/gi, "<br>")
    .replace(/<\s*br\s*\/?\s*>/gi, "<br>")
}

function decodeEntities(text) {
  return String(text || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
}

export function stripHtml(value) {
  return decodeEntities(
    normalizeHtml(value)
      .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n")
      .replace(/<br>/gi, "\n")
      .replace(/<li[^>]*>/gi, "\n- ")
      .replace(/<[^>]+>/g, " ")
  )
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim()
}

function splitRequirementLine(line) {
  return String(line || "")
    .split(/(?=-\s*Título)|(?=-\s*Haber)|(?=-\s*Certificado)/)
    .map((part) => part.replace(/^[-–]\s*/, "").trim())
    .filter(Boolean)
}

export function parseCourseDescription(html) {
  const rawHtml = normalizeHtml(html)
  const text = stripHtml(rawHtml)
  const lines = text.split("\n").map((line) => line.trim()).filter(Boolean)

  let expediente = ""
  let area = ""
  let academicLevel = ""
  let requisitosIntro = ""
  const requisitos = []
  const body = []
  let inReq = false

  for (const line of lines) {
    const exp = line.match(/^Exp:\s*(.+)$/i)
    if (exp) {
      expediente = exp[1].trim()
      continue
    }
    const areaMatch = line.match(/^Área:\s*(.+)$/i)
    if (areaMatch) {
      area = areaMatch[1].trim()
      continue
    }
    const levelMatch = line.match(/^Nivel académico requerido:\s*(.*)$/i)
    if (levelMatch) {
      academicLevel = levelMatch[1].trim()
      continue
    }
    if (/^Cumplir como mínimo/i.test(line) || /^Requisitos\s*:/i.test(line)) {
      inReq = true
      requisitosIntro = line.replace(/:\s*$/, "").trim()
      const after = line.includes(":") ? line.slice(line.indexOf(":") + 1).trim() : ""
      splitRequirementLine(after).forEach((item) => requisitos.push(item))
      continue
    }
    if (inReq) {
      const items = splitRequirementLine(line)
      if (items.length && (/^-/.test(line) || /Título de|Certificado|Haber superado/i.test(line))) {
        items.forEach((item) => requisitos.push(item))
        continue
      }
      inReq = false
    }
    if (!academicLevel && /Certificado de profesionalidad|Título de Técnico|Sin estudios/i.test(line)) {
      academicLevel = line.replace(/^[a-z]\s+/, "").trim()
      continue
    }
    body.push(line)
  }

  const summary = body[0] || ""
  const descriptionHtml = body
    .slice(1)
    .map((paragraph) => "<p>" + paragraph.replace(/</g, "&lt;") + "</p>")
    .join("")

  return {
    expediente,
    area,
    academicLevel,
    requisitosIntro,
    requisitos,
    summary,
    descriptionHtml,
    rawHtml,
    hasStructured: !!(expediente || area || requisitos.length),
  }
}

export function durationLabel(course) {
  const raw = course && course.duration
  if (raw == null || String(raw).trim() === "") return ""
  const text = String(raw).trim()
  if (/[a-zA-Z]/.test(text)) return text
  return text + " hours"
}

export function levelLabel(course) {
  const raw = course && course.eqf_nqf_level
  if (raw == null || String(raw).trim() === "" || String(raw) === "null") return ""
  const match = String(raw).match(/(\d+)/)
  if (!match) return String(raw)
  const level = Number(match[1])
  if (level <= 3) return "Beginner"
  if (level <= 5) return "Intermediate"
  return "Advanced"
}

export function modeLabel(course) {
  const raw = String(
    (course && (course.course_type_name || course.type || course.mode)) || ""
  )
  if (!raw) return ""
  if (/on.?site|presenc|face.?to.?face|classroom/i.test(raw)) return "In-person"
  if (/blend|hybrid|mix/i.test(raw)) return "Blended"
  if (/online|e-?learn|distance|remote|virtual|completo/i.test(raw)) return "Online"
  return raw
}

export function languageLabel(course) {
  let langs = course && course.languages
  if (typeof langs === "string") {
    try {
      langs = JSON.parse(langs)
    } catch (error) {
      return langs
    }
  }
  if (Array.isArray(langs)) return langs.filter(Boolean).join(", ")
  return (course && course.language) || ""
}

export function splitLearningOutcomes(value) {
  const raw = String(value || "").trim()
  if (!raw || raw === "null") return []

  if (/<li/i.test(raw)) {
    return Array.from(raw.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi))
      .map((match) => stripHtml(match[1]))
      .filter(Boolean)
  }

  const text = stripHtml(raw)
  const byLine = text
    .split(/\n+/)
    .map((line) => line.replace(/^[-*•]\s*/, "").trim())
    .filter(Boolean)
  if (byLine.length > 1) return byLine

  const byDash = text
    .split(/\s+-\s+/)
    .map((item) => item.replace(/^[-*•]\s*/, "").trim())
    .filter(Boolean)
  if (byDash.length > 1) return byDash

  const collapsed = text.replace(/\s+/g, " ").trim()
  const byTo = collapsed.split(/(?= To )/).map((item) => item.trim()).filter(Boolean)
  if (byTo.length > 1) return byTo

  return [collapsed]
}

export function splitChips(value) {
  if (!value) return []
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean)
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
}

export function displayTitle(course) {
  const title = (course && course.title) || ""
  return title.charAt(0).toUpperCase() + title.slice(1).toLowerCase()
}

export function costLabel(course) {
  const access = String((course && course.access) || "").toUpperCase()
  if (access === "FREE") return "Free"
  if (course && course.cost != null && course.cost !== "") return course.cost + " €"
  if (access === "PAID") return "Paid"
  return ""
}
